import { v4 as uuidv4 } from "uuid";

import createHash from "@framework/relay/server/network/helpers/create-hash";

declare global {
  interface Window {
    pageloadId?: string;
    ConcertConcierge?: {
      new (): { addPlugins: (plugins: object[]) => void };
    };
  }
}

interface APIInterface {
  disableRefresh?: object;
  reverbPlayer?: object;
}

interface FrameObjInterface {
  el: HTMLElement;
}

interface RequestInterface {
  refreshImmediately: string;
}

export const pageLoadUUID = (): string | null => {
  if (typeof window !== "undefined") {
    if (!window.pageloadId) {
      window.pageloadId = uuidv4();
    }

    return window.pageloadId;
  }

  return null;
};

export default pageLoadUUID;

/**
 * Handle Refresh Plugin
 * Allows for the ability to turn on or off refreshing at the ad level
 */
const refreshPlugin = {
  install: function (API: APIInterface) {
    API.disableRefresh = {
      method: this.handleRefreshRequest.bind(this),
      context: this,
    };

    return API;
  },

  handleRefreshRequest(
    frameObj: FrameObjInterface,
    request: RequestInterface,
    done: () => void,
  ) {
    const customEvent = request.refreshImmediately
      ? "refreshImmediately"
      : "refreshEligibilityToggle";

    const event = new CustomEvent(customEvent, {
      bubbles: true,
      detail: request,
    });

    frameObj.el.dispatchEvent(event);
    done();
  },
};

/**
 * Reverb Video Player plugin
 */
const reverbPlugin = {
  install: function (API: APIInterface) {
    API.reverbPlayer = {
      method: this.handleReverbPlugin.bind(this),
      context: this,
    };

    return API;
  },

  handleReverbPlugin(
    frameObj: FrameObjInterface,
    request: string,
    done: () => void,
  ) {
    const frameEl = frameObj.el;
    const container = document.createElement("div");
    container.id = "reverb-main";
    frameEl.parentNode?.insertBefore(container, frameEl);

    const playerScript = document.createElement("script");
    playerScript.type = "text/javascript";
    playerScript.src = `https://cdn.concert.io/reverb-video/${request}.js`;
    frameEl.parentNode?.insertBefore(playerScript, frameEl);

    frameEl.style.display = "none";

    done();
  },
};

/**
 * Installs Concert Concierge Plugins
 * The Concert Concierge offers a small API to Hymnal iframed ads
 * so the ad and lib can pass requests back and forth
 */
export const initConciergePlugins = (scriptLoadTimeout: number) => {
  if (!window.ConcertConcierge || Date.now() < scriptLoadTimeout) {
    setTimeout(initConciergePlugins, 100);
    return;
  }

  const concierge = new window.ConcertConcierge();

  concierge.addPlugins([refreshPlugin, reverbPlugin]);
};

/**
 * Installs new ad slots:
 * Useful for when new ads need to be triggered after initial page install
 */
export const installAds = () => {
  if (window.concertAds) {
    window.concertAds.insertSlots();
  }
};

/**
 * Hash result containing both SHA256 and SHA1 hashes
 */
export interface EmailHashes {
  sha256: string;
  sha1: string;
}

/**
 * Normalizes an email address by converting to lowercase and trimming whitespace.
 * This ensures consistent hashing regardless of input format.
 *
 * @param email - The email address to normalize
 * @returns Normalized email address
 */
export function normalizeEmail(email: string): string {
  if (!email || typeof email !== "string") {
    return "";
  }

  return email.trim().toLowerCase();
}

/**
 * Hashes an email address using both SHA256 and SHA1 algorithms.
 * The email is normalized (lowercased and trimmed) before hashing to ensure consistency.
 *
 * @param email - The email address to hash
 * @returns Promise resolving to an object containing both SHA256 and SHA1 hashes
 * @example
 * const hashes = await hashEmail("User@Example.com");
 * // { sha256: "abc123...", sha1: "def456..." }
 */
export async function hashEmail(email: string): Promise<EmailHashes> {
  const normalizedEmail = normalizeEmail(email);
  const [sha256, sha1] = await Promise.all([
    createHash({ text: normalizedEmail, hashType: "SHA-256" }),
    createHash({ text: normalizedEmail, hashType: "SHA-1" }),
  ]);

  return { sha256, sha1 };
}
